import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Tag, CheckCircle, XCircle, ArrowLeftRight } from 'lucide-react';
import { offersAPI, photoAPI, listingsAPI } from '../../lib/api';
import { useNotifications } from '../../context/NotificationsContext';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function MyOffersTab({ receivedOffers, setReceivedOffers, sentOffers, setSentOffers, setListings }) {
  const { t } = useTranslation();
  const { refresh: refreshNotifs } = useNotifications();
  const [counterForm, setCounterForm] = useState({ offerId: null, amount: '' });

  const handleOfferAction = async (offerId, action) => {
    try {
      if (action === 'accept') {
        await offersAPI.accept(offerId);
        toast.success(t('off.accepted_toast'));
      } else {
        await offersAPI.decline(offerId);
        toast.success(t('off.declined_toast'));
      }
      const [ro, l] = await Promise.all([offersAPI.getReceived(), listingsAPI.getMine()]);
      setReceivedOffers(ro.data.offers || []);
      setListings(l.data);
      refreshNotifs();
    } catch (err) { toast.error(err.response?.data?.detail || t('off.error')); }
  };

  const handleCounter = async () => {
    const amt = parseFloat(counterForm.amount);
    if (!amt) { toast.error(t('off.invalid_amount')); return; }
    try {
      await offersAPI.counter(counterForm.offerId, amt);
      toast.success(t('off.counter_sent'));
      setCounterForm({ offerId: null, amount: '' });
      const ro = await offersAPI.getReceived();
      setReceivedOffers(ro.data.offers || []);
    } catch (err) { toast.error(err.response?.data?.detail || t('off.error')); }
  };

  const handleBuyerOfferAction = async (offerId, action) => {
    try {
      if (action === 'accept-counter') {
        await offersAPI.acceptCounter(offerId);
        toast.success(t('off.counter_accepted_toast'));
      } else {
        await offersAPI.decline(offerId);
        toast.success(t('off.declined_toast'));
      }
      const so = await offersAPI.getSent();
      setSentOffers(so.data.offers || []);
      refreshNotifs();
    } catch (err) { toast.error(err.response?.data?.detail || t('off.error')); }
  };

  if (receivedOffers.length === 0 && sentOffers.length === 0) return (
    <div className="text-center py-16">
      <Tag className="w-12 h-12 text-[#B8D4E3] mx-auto mb-4" />
      <p className="text-[#5A8BA8]">{t('off.no_offers')}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {receivedOffers.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-[#033871] mb-3">{t('off.received')}</h3>
          <div className="space-y-2">
            {receivedOffers.map(o => (
              <div key={o.id} className="bg-white rounded-xl border border-[#D6E8F0] p-3 sm:p-4" data-testid={`received-offer-${o.id}`}>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#E8F4FD] overflow-hidden shrink-0">
                    {o.listing?.photos?.[0] && <img src={photoAPI.getUrl(o.listing.photos[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#033871] text-xs sm:text-sm truncate">{o.listing?.brand} {o.listing?.model}</p>
                    <p className="text-[10px] sm:text-xs text-[#5A8BA8]">{t('off.from', { name: o.buyer_name })}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-base sm:text-lg font-black text-[#0C63BF]">{o.amount.toFixed(0)}&euro;</p>
                    <p className="text-[10px] text-[#5A8BA8] line-through">{o.original_price.toFixed(0)}&euro;</p>
                  </div>
                </div>
                {o.status === 'pending' ? (
                  <div className="space-y-2 mt-3">
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                      <button onClick={() => handleOfferAction(o.id, 'accept')}
                        className="h-9 rounded-lg bg-[#32CD32] text-white text-xs sm:text-sm font-bold hover:bg-[#28a428] flex items-center justify-center gap-1 transition-colors"
                        data-testid={`accept-offer-${o.id}`}>
                        <CheckCircle className="w-3.5 h-3.5 shrink-0" /> <span className="hidden sm:inline">{t('off.accept')}</span><span className="sm:hidden">{t('off.ok')}</span>
                      </button>
                      <button onClick={() => setCounterForm({ offerId: o.id, amount: '' })}
                        className="h-9 rounded-lg bg-[#0C63BF] text-white text-xs sm:text-sm font-bold hover:bg-[#033871] flex items-center justify-center gap-1 transition-colors"
                        data-testid={`counter-offer-btn-${o.id}`}>
                        <ArrowLeftRight className="w-3.5 h-3.5 shrink-0" /> <span className="hidden sm:inline">{t('off.counter')}</span><span className="sm:hidden">{t('off.negotiate')}</span>
                      </button>
                      <button onClick={() => handleOfferAction(o.id, 'decline')}
                        className="h-9 rounded-lg border-2 border-red-200 text-red-500 text-xs sm:text-sm font-bold hover:bg-red-50 flex items-center justify-center gap-1 transition-colors"
                        data-testid={`decline-offer-${o.id}`}>
                        <XCircle className="w-3.5 h-3.5 shrink-0" /> <span className="hidden sm:inline">{t('off.decline')}</span><span className="sm:hidden">{t('off.no')}</span>
                      </button>
                    </div>
                    {counterForm.offerId === o.id && (
                      <div className="bg-[#F0F7FA] rounded-xl p-3 space-y-2 border border-[#E8F4FD]" data-testid={`counter-form-${o.id}`}>
                        <p className="text-xs font-bold text-[#033871]">{t('off.propose_between', { min: o.amount.toFixed(0), max: o.original_price.toFixed(0) })}</p>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <input type="number" value={counterForm.amount} onChange={e => setCounterForm(prev => ({ ...prev, amount: e.target.value }))}
                              placeholder={t('off.ex_placeholder', { amount: Math.round((o.amount + o.original_price) / 2) })}
                              className="w-full h-10 pl-3 pr-8 rounded-lg border border-[#D6E8F0] text-sm font-bold text-[#033871] focus:border-[#0C63BF] outline-none"
                              data-testid={`counter-amount-${o.id}`} />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#5A8BA8]">&euro;</span>
                          </div>
                          <button onClick={handleCounter}
                            className="px-4 h-10 rounded-lg bg-[#32CD32] text-white text-sm font-bold hover:bg-[#28a428] transition-colors"
                            data-testid={`send-counter-${o.id}`}>{t('off.send')}</button>
                          <button onClick={() => setCounterForm({ offerId: null, amount: '' })}
                            className="px-3 h-10 rounded-lg border border-[#D6E8F0] text-[#5A8BA8] text-sm hover:bg-white transition-colors">
                            &times;
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      o.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      o.status === 'countered' ? 'bg-blue-100 text-blue-700' :
                      'bg-red-100 text-red-700'
                    }`}>{o.status === 'accepted' ? t('off.status_accepted') : o.status === 'countered' ? t('off.status_countered', { amount: o.counter_amount?.toFixed(0) }) : t('off.status_declined')}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {sentOffers.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-[#033871] mb-3">{t('off.sent_title')}</h3>
          <div className="space-y-2">
            {sentOffers.map(o => (
              <div key={o.id} className="bg-white rounded-xl border border-[#D6E8F0] p-3 sm:p-4" data-testid={`sent-offer-${o.id}`}>
                <Link to={`/annonce/${o.listing_id}`} className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#E8F4FD] overflow-hidden shrink-0">
                    {o.listing?.photos?.[0] && <img src={photoAPI.getUrl(o.listing.photos[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#033871] text-xs sm:text-sm truncate">{o.listing?.brand} {o.listing?.model}</p>
                    <p className="text-[10px] sm:text-xs text-[#5A8BA8]">{t('off.my_offer')} <span className="font-bold text-[#0C63BF]">{o.amount.toFixed(0)}&euro;</span> <span className="line-through">{o.original_price.toFixed(0)}&euro;</span></p>
                  </div>
                  <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium shrink-0 ${
                    o.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    o.status === 'accepted' ? 'bg-green-100 text-green-700' :
                    o.status === 'countered' ? 'bg-blue-100 text-blue-700' :
                    'bg-red-100 text-red-700'
                  }`}>{o.status === 'pending' ? t('off.status_pending') : o.status === 'accepted' ? t('off.status_accepted') : o.status === 'countered' ? t('off.status_counter_short') : t('off.status_declined')}</span>
                </Link>
                {o.status === 'countered' && (
                  <div className="mt-3 bg-[#F0F7FA] rounded-xl p-2.5 sm:p-3 border border-[#E8F4FD]" data-testid={`counter-response-${o.id}`}>
                    <p className="text-xs sm:text-sm text-[#033871]">
                      {t('off.seller_proposes')} <span className="font-black text-[#0C63BF] text-base sm:text-lg">{o.counter_amount?.toFixed(0)}&euro;</span>
                      <span className="text-[#5A8BA8] text-[10px] sm:text-xs ml-1">{t('off.your_offer', { amount: o.amount.toFixed(0) })}</span>
                    </p>
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mt-2">
                      <button onClick={() => handleBuyerOfferAction(o.id, 'accept-counter')}
                        className="h-9 rounded-lg bg-[#32CD32] text-white text-xs sm:text-sm font-bold hover:bg-[#28a428] flex items-center justify-center gap-1 transition-colors"
                        data-testid={`accept-counter-${o.id}`}>
                        <CheckCircle className="w-3.5 h-3.5" /> {t('off.accept')}
                      </button>
                      <button onClick={() => handleBuyerOfferAction(o.id, 'decline')}
                        className="h-9 rounded-lg border-2 border-red-200 text-red-500 text-xs sm:text-sm font-bold hover:bg-red-50 flex items-center justify-center gap-1 transition-colors"
                        data-testid={`decline-counter-${o.id}`}>
                        <XCircle className="w-3.5 h-3.5" /> {t('off.decline')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
